import type { IncomingMessage } from "http";
import type { TLSSocket } from "tls";

/* ------------------------------------------------------------------ *
 * PLATFORM IDENTITY
 *
 * The platform identity is centralized here, so none of this may be a
 * literal: the footer's "Powered by" link, the app name and the slug all
 * change together, and a literal is a find-and-replace waiting to be missed.
 *
 * NEXT_PUBLIC_* is inlined at BUILD time, so changing any of these needs a
 * rebuild of :3002 — unlike the API origin below, which is resolved from
 * `window.location` at call time precisely so one build serves every host.
 * That is the right trade here: the brand does not vary by host, the API
 * origin does.
 *
 *   NEXT_PUBLIC_MARKETING_WEBSITE UNSET => https://zenvix.cloud, matching the
 *     API's MARKETING_WEBSITE default, which prints the same brand on receipts
 *     (mongo-api/src/lib/receiptEngine.js).
 *   NEXT_PUBLIC_APP_NAME  UNSET => "Zenvix Restaurant".
 *   NEXT_PUBLIC_APP_SLUG  UNSET => "zenvix-restaurant".
 * ------------------------------------------------------------------ */
export const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || "Zenvix Restaurant";
export const APP_SLUG = process.env.NEXT_PUBLIC_APP_SLUG || "zenvix-restaurant";

/** The platform marketing site. Used by the footer's "Powered by" link. */
export const PLATFORM_WEBSITE =
  process.env.NEXT_PUBLIC_MARKETING_WEBSITE || "https://zenvix.cloud";

/** @deprecated Kept as an alias of PLATFORM_WEBSITE; both were the same literal. */
export const APP_WEBSITE = PLATFORM_WEBSITE;

/**
 * WhatsApp deep-link base (ContactFabs "chat" button). The NUMBER stays a
 * tenant setting — it comes from the tenant's own contact details — but the
 * vendor host is configurable so a regional/alternate host is an .env edit.
 *   UNSET => https://wa.me, the standard host.
 */
export const WHATSAPP_LINK_BASE = (
  process.env.NEXT_PUBLIC_WHATSAPP_LINK_BASE || "https://wa.me"
).replace(/\/+$/, "");

export const TENANT_ID = process.env.NEXT_PUBLIC_TENANT_ID || "smash-d-dha2";

/* ------------------------------------------------------------------ *
 * ADDITION — the BRANCH this browser tab is currently on.
 *
 * `TENANT_ID` is inlined at build time and is the fallback default for
 * every fetcher in `src/lib/api.ts`. Once one deployment can serve any
 * branch (see `src/lib/branch.ts`) that default is no longer the whole
 * answer: a fetcher called with no explicit tenant must ask for the
 * branch the customer is actually looking at.
 *
 * Almost every client call already reads `settings.data._id` out of the
 * store, which follows the branch for free because the settings come
 * from the same server render. This covers the handful that fall
 * through to the default instead.
 *
 * DELIBERATELY BROWSER-ONLY. In Node this module is shared by every
 * concurrent request, so a mutable "current tenant" would leak one
 * visitor's branch into another's server render — the classic
 * module-level-state bug. `setActiveTenantId` is a no-op on the server,
 * where `getServerSideProps` passes the tenant explicitly instead.
 * ------------------------------------------------------------------ */

let ACTIVE_TENANT_ID = TENANT_ID;

/** The branch to use when a caller names none. `TENANT_ID` until set. */
export const activeTenantId = (): string => ACTIVE_TENANT_ID;

/** Called once per page from `_app`, with the server-resolved branch. */
export const setActiveTenantId = (tenantId?: string | null): void => {
  if (typeof window === "undefined") return;
  if (!tenantId) return;
  ACTIVE_TENANT_ID = tenantId;
};

/* ------------------------------------------------------------------ *
 * Where the ordering API lives.
 *
 * `NEXT_PUBLIC_*` values are inlined into the CLIENT bundle at build
 * time, so a hardcoded `https://localhost:4001` resolves to the
 * VISITOR's own machine as soon as anyone opens the storefront from
 * another device. The platform's own answer (see the recovered POS,
 * whose .env carries `VITE_LOCAL_BACKEND_PORT=4001` and nothing else) is
 * to configure a PORT and derive the host from the browser's current
 * location. That works unchanged on localhost, on the LAN IP and on the
 * public IP.
 *
 * Precedence:
 *   1. `NEXT_PUBLIC_API_BASE_URL` — explicit, for when the API genuinely
 *      lives on another host. Overrides everything, on both sides.
 *   2. `API_SERVER_BASE_URL` — server-only override (not inlined into the
 *      bundle), for when SSR should reach the API by a different route
 *      than the browser does.
 *   3. Derived: `<protocol>://<current hostname>:<port>` in the browser,
 *      `<protocol>://localhost:<port>` in Node, where `getServerSideProps`
 *      runs on the same box as the API.
 * ------------------------------------------------------------------ */

const EXPLICIT_API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "";
const RAW_API_PORT = process.env.NEXT_PUBLIC_API_PORT || "";
const API_PORT = RAW_API_PORT || "4001";
const API_PROTOCOL = process.env.NEXT_PUBLIC_API_PROTOCOL || "https";

/**
 * Same-origin path the BROWSER sends API calls to, proxied on to :4001 by
 * `src/pages/api/backend/[...path].ts`.
 *
 * Deriving `https://<page hostname>:4001` in the browser fixed the localhost
 * bug but left a second one that only a REMOTE visitor ever sees: :3002 and
 * :4001 are separate origins, and a self-signed-certificate exception is
 * stored PER ORIGIN. The visitor clicks through the interstitial for the page
 * and gets no interstitial at all for the API — `fetch()` to an untrusted
 * certificate is rejected outright — so the site painted and then did nothing.
 *
 * Going through this app's own origin means one certificate to accept instead
 * of two, no cross-origin request and therefore no CORS dependency, and no
 * mixed content. SSR is untouched: `getServerSideProps` runs in Node on the
 * same box as the API and still calls :4001 over loopback directly.
 *
 * Set `NEXT_PUBLIC_API_PROXY_PATH=""` to go back to calling :4001 straight
 * from the browser — correct only when the API is behind a certificate the
 * visitor's browser already trusts.
 */
const API_PROXY_PATH =
  process.env.NEXT_PUBLIC_API_PROXY_PATH === undefined
    ? "/api/backend"
    : process.env.NEXT_PUBLIC_API_PROXY_PATH;

/**
 * True when this build is pointed at a real ordering API — either by an
 * explicit base URL or by a port to derive one from. Setting the port
 * alone is the normal case.
 */
export const API_CONFIGURED =
  Boolean(EXPLICIT_API_BASE_URL) || Boolean(RAW_API_PORT);

/**
 * The API origin, resolved at CALL time rather than at build time. Never
 * hoist this into a module-level constant — that is exactly the bug it
 * exists to fix.
 *
 * In the browser this is normally a same-origin PATH, not an absolute origin
 * (see `API_PROXY_PATH`); `src/lib/api.ts` joins it to `/web/...` and hands
 * the result to `fetch`, which resolves a relative URL against the page.
 */
export const apiBaseUrl = (): string => {
  if (EXPLICIT_API_BASE_URL) return EXPLICIT_API_BASE_URL;
  if (!API_CONFIGURED) return "";
  if (typeof window === "undefined") {
    return (
      process.env.API_SERVER_BASE_URL || `${API_PROTOCOL}://localhost:${API_PORT}`
    );
  }
  if (API_PROXY_PATH) return API_PROXY_PATH.replace(/\/+$/, "");
  return `${API_PROTOCOL}://${window.location.hostname}:${API_PORT}`;
};

/* ------------------------------------------------------------------ *
 * The site's own origin, used for canonical / og:url / sitemap.
 *
 * The original derives this at runtime too — its SeoHead builds the
 * canonical from `window.location.host` + `window.location.protocol`,
 * with an optional explicit override passed in as a prop. We keep that
 * shape, and additionally derive it from the request host during SSR so
 * the served HTML carries a correct canonical on whatever host the
 * visitor used. Set `NEXT_PUBLIC_BASE_URL` to pin a public domain.
 * ------------------------------------------------------------------ */

export const SITE_BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || "";

/* ------------------------------------------------------------------ *
 * Firebase Storage media base.
 *
 * Shared image assets (src/constants/images.ts) used to carry the bucket
 * and host in full, once per asset. A bucket change — as happened when
 * prod-managesome was retired — then meant a grep across the source
 * instead of one .env edit.
 *
 *   NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET            bucket name
 *   NEXT_PUBLIC_FIREBASE_STORAGE_MEDIA_BASE_URL    full base, overrides the above
 *
 * UNSET (both) => this project's current bucket, so the storefront keeps
 * working out of the box. It is a default rather than a required value
 * because these are public download URLs, not credentials.
 * ------------------------------------------------------------------ */

const FIREBASE_STORAGE_BUCKET =
  process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET ||
  "online-pos-7a275.firebasestorage.app";

export const FIREBASE_STORAGE_MEDIA_BASE_URL = (
  process.env.NEXT_PUBLIC_FIREBASE_STORAGE_MEDIA_BASE_URL ||
  `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_STORAGE_BUCKET}/o`
).replace(/\/+$/, "");

/** Build a Firebase Storage download URL from an already-encoded object path. */
export const firebaseMediaUrl = (pathAndQuery: string): string =>
  `${FIREBASE_STORAGE_MEDIA_BASE_URL}/${String(pathAndQuery).replace(/^\/+/, "")}`;

/* ------------------------------------------------------------------ *
 * STARTUP BANNER — keep it.
 *
 * When the internal dashboard's shim silently pointed at the retired
 * :4000, its banner was the only visible evidence of where calls were
 * actually going, and reading it is what identified the bug. This prints
 * the same thing for the storefront: the origin it has resolved for THIS
 * host, and which variable each part came from.
 *
 * Browser-only and guarded, so SSR logs stay quiet and the banner shows
 * the host the visitor actually used.
 * ------------------------------------------------------------------ */
if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.log(
    `[zenvix-restaurant-web] API -> ${apiBaseUrl() || "(not configured — serving the static snapshot)"}` +
      (EXPLICIT_API_BASE_URL
        ? "  (origin: NEXT_PUBLIC_API_BASE_URL)"
        : !API_CONFIGURED
          ? "  (NEXT_PUBLIC_API_PORT and NEXT_PUBLIC_API_BASE_URL both unset)"
          : API_PROXY_PATH
            ? `  (same-origin proxy -> :${API_PORT}; one certificate, no CORS. Set NEXT_PUBLIC_API_PROXY_PATH="" for a direct call)`
            : `  (host: page, port: NEXT_PUBLIC_API_PORT, protocol: ${
                process.env.NEXT_PUBLIC_API_PROTOCOL ? "NEXT_PUBLIC_API_PROTOCOL" : "default https"
              })`),
  );
}

type OriginRequest = Pick<IncomingMessage, "headers" | "socket">;

const headerValue = (value?: string | string[]): string | undefined =>
  (Array.isArray(value) ? value[0] : value)?.split(",")[0]?.trim() || undefined;

/**
 * `req` in `getServerSideProps`, nothing in the browser. Returns "" when
 * the origin cannot be determined, which callers treat as "emit no
 * canonical" rather than emitting a wrong one.
 */
export const siteOrigin = (req?: OriginRequest): string => {
  if (SITE_BASE_URL) return SITE_BASE_URL;
  if (req) {
    const host = headerValue(req.headers.host);
    if (!host) return "";
    const protocol =
      headerValue(req.headers["x-forwarded-proto"]) ??
      ((req.socket as TLSSocket | undefined)?.encrypted ? "https" : "http");
    return `${protocol}://${host}`;
  }
  if (typeof window !== "undefined") return window.location.origin;
  return "";
};

/**
 * Demo backend. On by default so the app is clickable end to end out of the
 * box; set NEXT_PUBLIC_USE_MOCK_API=false (and provide NEXT_PUBLIC_API_PORT,
 * or NEXT_PUBLIC_API_BASE_URL for a remote API) to talk to a real server.
 */
export const USE_MOCK_API =
  process.env.NEXT_PUBLIC_USE_MOCK_API !== "false" && !API_CONFIGURED;

/** True when there is neither a real API nor the mock — static snapshot only. */
export const IS_OFFLINE_MODE = !API_CONFIGURED && !USE_MOCK_API;

/**
 * Analytics.
 *
 * The original reads these from the tenant's own settings (`gtm_id`,
 * `ms_clarity_id`, `fb_pixel_id`) and falls back to a Clarity id hardcoded in
 * its bundle. We deliberately do NOT reuse that id, or any of smashd-dha's:
 * pointing our traffic at their property would pollute their analytics. Ours
 * come from the environment, and each script is a no-op while its variable is
 * unset — so the default build ships no third-party tracking at all.
 */
export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID || "";
export const GA_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "";
export const CLARITY_ID = process.env.NEXT_PUBLIC_CLARITY_ID || "";
export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID || "";
